/**
 * O rosto de uma pessoa do time, em qualquer lugar do produto.
 *
 * <p>Existe porque a foto de perfil precisava aparecer em <b>dezessete</b> lugares que
 * desenhavam o avatar à mão, cada um repetindo o mesmo
 * `<span style={{ background: cor }}>{iniciais(nome)}</span>`. É a mesma história do
 * `iniciais` e do `tamanhoLegivel`: enquanto era só cor e duas letras, dezessete cópias
 * eram feias mas funcionavam; no dia em que a regra passou a ter um "se tem foto",
 * dezessete cópias viraram dezessete lugares para esquecer.
 *
 * <p><b>A cor não sai de cena quando há foto.</b> Ela continua sendo a identidade da
 * pessoa nos gráficos da Equipe, onde não há avatar nenhum para desenhar, e é o que
 * aparece enquanto a imagem não chegou.
 *
 * <p>Cada lugar mantém a própria classe: o tamanho, o raio e a borda do avatar do topo
 * não são os do avatar de um comentário. O que este componente unifica é a <b>regra</b>
 * — foto quando existe, iniciais quando não —, não o desenho.
 */
import type { CSSProperties } from 'react';

import { iniciais } from './iniciais';
import { useImagemAutenticada } from './ImagemAutenticada';

export function Avatar({
  nome,
  cor,
  url,
  className,
  style,
  title,
}: {
  nome: string | null | undefined;
  /** A cor do time. Vira o fundo das iniciais e o fundo enquanto a foto carrega. */
  cor?: string | null;
  /** A url em `/api/arquivos/…`, ou nada para quem não subiu foto. */
  url?: string | null;
  className: string;
  style?: CSSProperties;
  title?: string;
}) {
  // A wiki é interna: `<img src>` não manda o token, então a imagem vem por fetch
  // autenticado e vira um object URL. Ver `ImagemAutenticada`.
  const imagem = useImagemAutenticada(url);

  return (
    <span
      className={`${className} av-comum${imagem ? ' com-foto' : ''}`}
      title={title ?? nome ?? undefined}
      style={
        imagem
          ? { ...style, backgroundImage: `url('${imagem}')` }
          : { ...style, background: cor ?? 'var(--meta)' }
      }
    >
      {/*
        As iniciais continuam no DOM mesmo com foto, e somem por CSS.

        É o que faz a troca não piscar: a foto chega depois do primeiro desenho, e
        remover o texto no mesmo instante em que o fundo aparece deixaria o avatar
        vazio por um quadro. Some também para o leitor de tela, que já lê o `title`.
      */}
      <span className="av-iniciais" aria-hidden={imagem ? true : undefined}>
        {iniciais(nome)}
      </span>
    </span>
  );
}
